/* eslint-disable consistent-return */
import React, {
	useCallback,
	useEffect,
	useMemo,
	useState,
	Dispatch,
} from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { CheckBox, MultiSelect, theme } from "uikit";

import Language from "../../../../../../../../../services/Language";
import TaxiService from "../../../../../../../../../services/TaxiService";
import Company from "../../../../../../../../../services/Company";
import { ParkingFilter } from "../../../../../../../../../redux/reducers/OrdersPage/Filters";
import {
	useCarBrandSubscription,
	useCheckFlagAccessControl,
} from "../../../../../../../../../hooks";
import { TabAccessNames, TabKeys } from "../../../../../../constants/access";
import StoreList from "../../../../../../../../../types/StoreList";
import {
	MultiSelectExecutorGroupWithModal,
	StyledColumn,
	StyledRow,
	StyledGrid,
	AccessGuard,
} from "../../../../../../../../common";
import FieldsContainer from "../../../../../../../../FieldsContainer";
import LabeledField from "../../../../../../../../LabeledField";
import FieldsContainerCustom from "../../../../../ParkingsModal/components/FieldsContainer";

import TablePosition from "./components/TablePosition";

const CheckboxText = styled("span")`
	font-size: 14px;
	color: ${theme.colors.text_placeholder};
	cursor: default;
	line-height: 20px;
`;

const Content: React.FC<Content.Props> = ({
	taxiServiceData,
	taxiServiceIdsPosition,
	language,
	onChangeRequestData,
	possibleCompanies,
	possibleTaxiServices,
	companyIds,
	taxiServiceIds,
	setCompanyIds,
	setTaxiServiceIds,
	taxiServiceIdsTablePosition,
	setTaxiServiceIdsTablePosition,
	executorsStatuses,
	setExecutorsStatuses,
	error,
	onChangeError,
	onlineStatus,
	setOnlineStatus,
	workingStatus,
	setWorkingStatus,
	isYourOrderStatus,
	setIsYourOrderStatus,
	executorGroupIds,
	setExecutorGroupIds,
	setShowModalPropsForExecutorGroup,
}) => {
	const { t } = useTranslation();
	const checkFlagAccessControl = useCheckFlagAccessControl(TabAccessNames);

	const possibleCompanyOptions = useMemo(
		() =>
			possibleCompanies.map((company) => ({
				key: company.id,
				label: company.name?.[language],
				value: company.id,
			})),
		[language, possibleCompanies],
	);

	const companyIdsOnChange = useCallback(
		(newCompanyIds: MultiSelect.Value) => {
			setCompanyIds(newCompanyIds as number[]);
			onChangeError((prev) => ({ ...prev, company: false }));
		},
		[onChangeError, setCompanyIds],
	);

	const possibleTaxiServiceOptions = useMemo(
		() =>
			possibleTaxiServices.map((taxiService) => ({
				key: taxiService.id,
				label: `${taxiService?.settlement?.[language]} (${taxiService.company?.name?.[language]}) `,
				value: taxiService.id,
			})),
		[language, possibleTaxiServices],
	);

	const taxiServiceIdsOnChange = useCallback(
		(newCompanyIds: MultiSelect.Value) => {
			setTaxiServiceIds(newCompanyIds as number[]);
			onChangeError((prev) => ({ ...prev, taxiService: false }));
		},
		[onChangeError, setTaxiServiceIds],
	);

	const tablePositionData = useMemo(
		() =>
			taxiServiceIdsTablePosition.map((taxiServiceId) => {
				const taxiService = taxiServiceData?.find(
					(service) => service.id === taxiServiceId,
				);
				return {
					id: taxiService?.id,
					name: `${taxiService?.company?.name?.[language]} (${taxiService?.settlement?.[language]})`,
				};
			}),
		[language, taxiServiceData, taxiServiceIdsTablePosition],
	);

	const [showCompany, setShowCompany] = useState(false);
	const [showTaxiService, setShowTaxiService] = useState(false);
	const openCompany = useCallback(() => {
		setShowCompany(true);
	}, []);

	const closeCompany = useCallback(() => {
		setShowCompany(false);
	}, []);

	const openTaxiService = useCallback(() => {
		setShowTaxiService(true);
	}, []);

	const closeTaxiService = useCallback(() => {
		setShowTaxiService(false);
	}, []);

	const onFocusCompany = useCallback(() => {
		openCompany();
		closeTaxiService();
	}, [closeTaxiService, openCompany]);

	const onFocusTaxiService = useCallback(() => {
		openTaxiService();
		closeCompany();
	}, [closeCompany, openTaxiService]);

	const closeCompanyAndTaxiService = useCallback(() => {
		closeCompany();
		closeTaxiService();
	}, [closeCompany, closeTaxiService]);

	const isAvailable = useMemo(
		() => executorsStatuses?.includes("available"),
		[executorsStatuses],
	);
	const isBusy = useMemo(
		() => executorsStatuses?.includes("busy"),
		[executorsStatuses],
	);
	const isDinner = useMemo(
		() => executorsStatuses?.includes("dinner"),
		[executorsStatuses],
	);
	const isHome = useMemo(
		() => executorsStatuses?.includes("home"),
		[executorsStatuses],
	);
	const isOnOrder = useMemo(
		() => executorsStatuses?.includes("on_order"),
		[executorsStatuses],
	);
	const isClosed = useMemo(
		() => executorsStatuses?.includes("closed"),
		[executorsStatuses],
	);

	const onChageStatusesCheckbox = useCallback(
		(value: boolean, status: Content.ExecutorStatus) => {
			if (value && !executorsStatuses?.includes(status)) {
				setExecutorsStatuses([...executorsStatuses, status]);
			} else if (!value && executorsStatuses?.includes(status)) {
				setExecutorsStatuses([
					...executorsStatuses.filter((el) => el !== status),
				]);
			}
		},
		[executorsStatuses, setExecutorsStatuses],
	);

	useEffect(() => {
		onChangeRequestData({
			taxiServiceIds,
			companyIds,
			taxiServiceIdsPosition: taxiServiceIdsTablePosition.filter((id) =>
				taxiServiceIds.includes(id),
			),
			executorGroupIds,
			statuses: executorsStatuses,
			online: onlineStatus,
			workingStatus,
			isYourOrderStatus,
		});
	}, [
		companyIds,
		executorGroupIds,
		executorsStatuses,
		isYourOrderStatus,
		onChangeRequestData,
		onlineStatus,
		taxiServiceIds,
		taxiServiceIdsPosition,
		taxiServiceIdsTablePosition,
		workingStatus,
	]);

	const onChageOnlineCheckbox = useCallback(
		(value: boolean, status: Content.OnlineStatus) => {
			if (value && !onlineStatus?.includes(status)) {
				setOnlineStatus([...onlineStatus, status]);
			} else if (!value && onlineStatus?.includes(status)) {
				setOnlineStatus([
					...onlineStatus.filter((el) => el !== status),
				]);
			}
		},
		[onlineStatus, setOnlineStatus],
	);

	const isOnline = useMemo(
		() => onlineStatus.includes("online"),
		[onlineStatus],
	);
	const isOffline = useMemo(
		() => onlineStatus.includes("offline"),
		[onlineStatus],
	);

	const onChangeOnline = useCallback(
		(value) => {
			onChageOnlineCheckbox(value, "online");
			closeCompanyAndTaxiService();
		},
		[closeCompanyAndTaxiService, onChageOnlineCheckbox],
	);
	const onChangeOffline = useCallback(
		(value) => {
			onChageOnlineCheckbox(value, "offline");
			closeCompanyAndTaxiService();
		},
		[closeCompanyAndTaxiService, onChageOnlineCheckbox],
	);

	const onChangeAvailable = useCallback(
		(value) => {
			onChageStatusesCheckbox(value, "available");
			closeCompanyAndTaxiService();
		},
		[closeCompanyAndTaxiService, onChageStatusesCheckbox],
	);
	const onChangeBusy = useCallback(
		(value) => {
			onChageStatusesCheckbox(value, "busy");
			closeCompanyAndTaxiService();
		},
		[closeCompanyAndTaxiService, onChageStatusesCheckbox],
	);
	const onChangeDinner = useCallback(
		(value) => {
			onChageStatusesCheckbox(value, "dinner");
			closeCompanyAndTaxiService();
		},
		[closeCompanyAndTaxiService, onChageStatusesCheckbox],
	);
	const onChangeHome = useCallback(
		(value) => {
			onChageStatusesCheckbox(value, "home");
			closeCompanyAndTaxiService();
		},
		[closeCompanyAndTaxiService, onChageStatusesCheckbox],
	);
	const onChangeOnOrder = useCallback(
		(value) => {
			onChageStatusesCheckbox(value, "on_order");
			closeCompanyAndTaxiService();
		},
		[closeCompanyAndTaxiService, onChageStatusesCheckbox],
	);
	const onChangeClosed = useCallback(
		(value) => {
			onChageStatusesCheckbox(value, "closed");
			closeCompanyAndTaxiService();
		},
		[closeCompanyAndTaxiService, onChageStatusesCheckbox],
	);

	const onChageWorkingStatusCheckbox = useCallback(
		(value: boolean, status: Content.WorkingStatus) => {
			if (value && !workingStatus?.includes(status)) {
				setWorkingStatus([...workingStatus, status]);
			} else if (!value && workingStatus?.includes(status)) {
				setWorkingStatus([
					...workingStatus.filter((el) => el !== status),
				]);
			}
		},
		[setWorkingStatus, workingStatus],
	);

	const isWorkingStatus = useMemo(
		() => workingStatus.includes("working"),
		[workingStatus],
	);
	const isNotWorkingStatus = useMemo(
		() => workingStatus.includes("notWorking"),
		[workingStatus],
	);

	const onChangeWorking = useCallback(
		(value) => {
			onChageWorkingStatusCheckbox(value, "working");
			closeCompanyAndTaxiService();
		},
		[closeCompanyAndTaxiService, onChageWorkingStatusCheckbox],
	);
	const onChangeNotWorking = useCallback(
		(value) => {
			onChageWorkingStatusCheckbox(value, "notWorking");
			closeCompanyAndTaxiService();
		},
		[closeCompanyAndTaxiService, onChageWorkingStatusCheckbox],
	);

	const onChangeIsYourOrderStatus = useCallback(
		(value) => {
			setIsYourOrderStatus(value);
			closeCompanyAndTaxiService();
		},
		[closeCompanyAndTaxiService, setIsYourOrderStatus],
	);

	useEffect(() => {
		if (possibleCompanyOptions.length === 1)
			setCompanyIds([possibleCompanyOptions[0].key]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [possibleCompanyOptions]);
	useEffect(() => {
		if (possibleTaxiServiceOptions.length === 1)
			setTaxiServiceIds([possibleTaxiServiceOptions[0].key]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [possibleTaxiServiceOptions]);

	return (
		<StyledGrid areas="" gap="12px" columns="1fr 1.5fr 1fr 1fr">
			<StyledColumn gap="12px" w="100%">
				<LabeledField
					label={
						t(
							"orderPageWidgets.parking.filter.popover.content.str0",
						) ?? ""
					}
				>
					<MultiSelect
						value={companyIds}
						disabled={
							!checkFlagAccessControl[
								TabKeys.COMPANIES_AND_TAXI_SERVICES
							] || possibleCompanyOptions.length === 1
						}
						all={possibleCompanyOptions.length > 1}
						// open={showCompany}
						// focused={showCompany}
						// onFocus={onFocusCompany}
						// onBlur={closeCompany}
						placeholder={
							t(
								"orderPageWidgets.parking.filter.popover.content.str1",
							) ?? ""
						}
						error={error.company}
						options={possibleCompanyOptions}
						onChange={companyIdsOnChange}
					/>
				</LabeledField>
				<LabeledField
					label={
						t(
							"orderPageWidgets.parking.filter.popover.content.str2",
						) ?? ""
					}
				>
					<MultiSelect
						value={taxiServiceIds}
						disabled={
							!checkFlagAccessControl[
								TabKeys.COMPANIES_AND_TAXI_SERVICES
							] || possibleTaxiServiceOptions.length <= 1
						}
						all={possibleTaxiServiceOptions.length > 1}
						// open={showTaxiService}
						// focused={showTaxiService}
						// onFocus={onFocusTaxiService}
						// onBlur={closeTaxiService}
						placeholder={
							t(
								"orderPageWidgets.parking.filter.popover.content.str3",
							) ?? ""
						}
						error={error.taxiService}
						options={possibleTaxiServiceOptions}
						onChange={taxiServiceIdsOnChange}
					/>
				</LabeledField>
				<LabeledField
					label={
						t(
							"orderPageWidgets.chat.executorsFilterPanel.popover.str201",
						) ?? ""
					}
				>
					<MultiSelectExecutorGroupWithModal
						value={executorGroupIds || []}
						disabled={
							!checkFlagAccessControl[TabKeys.EXECUTOR_TEAMS]
						}
						onChange={setExecutorGroupIds}
						titleText={
							t(
								"orderPageWidgets.chat.executorsFilterPanel.popover.str201",
							) ?? ""
						}
						modalSetting={{
							zIndex: 999,
							containerId: "root",
						}}
						setShowModalProps={setShowModalPropsForExecutorGroup}
					/>
				</LabeledField>
			</StyledColumn>
			<AccessGuard
				accessName={TabAccessNames[TabKeys.PARKING_GROUP_LIST]}
				fallbackWrapper
			>
				<StyledColumn onClick={closeCompanyAndTaxiService}>
					<FieldsContainerCustom
						label={
							t(
								"orderPageWidgets.parking.filter.popover.content.str4",
							) ?? ""
						}
						style={{ height: 302 }}
						labelColor={theme.colors.text_placeholder}
					>
						<TablePosition
							data={tablePositionData}
							taxiServiceIdsTablePosition={
								taxiServiceIdsTablePosition
							}
							onChangeTaxiServiceTablePosition={
								setTaxiServiceIdsTablePosition
							}
						/>
					</FieldsContainerCustom>
				</StyledColumn>
			</AccessGuard>
			<StyledColumn onClick={closeCompanyAndTaxiService} w="100%">
				<FieldsContainer
					label={
						t(
							"orderPageWidgets.parking.filter.popover.content.str5",
						) ?? ""
					}
					labelColor={theme.colors.text_placeholder}
				>
					<StyledColumn gap="12px">
						<StyledRow gap="10px">
							<CheckBox
								onChange={onChangeOnline}
								value={isOnline}
								disabled={
									!checkFlagAccessControl[TabKeys.STATUS]
								}
							/>
							<CheckboxText>
								{t(
									"orderPageWidgets.parking.filter.popover.content.str100",
								) ?? ""}
							</CheckboxText>
						</StyledRow>
						<StyledRow gap="10px">
							<CheckBox
								onChange={onChangeOffline}
								value={isOffline}
								disabled={
									!checkFlagAccessControl[TabKeys.STATUS]
								}
							/>
							<CheckboxText>
								{t(
									"orderPageWidgets.parking.filter.popover.content.str101",
								) ?? ""}
							</CheckboxText>
						</StyledRow>
						<StyledRow gap="10px">
							<CheckBox
								onChange={onChangeAvailable}
								value={isAvailable}
								disabled={
									!checkFlagAccessControl[TabKeys.STATUS]
								}
							/>
							<CheckboxText>
								{t(
									"orderPageWidgets.parking.filter.popover.content.str102",
								) ?? ""}
							</CheckboxText>
						</StyledRow>
						<StyledRow gap="10px">
							<CheckBox
								onChange={onChangeBusy}
								value={isBusy}
								disabled={
									!checkFlagAccessControl[TabKeys.STATUS]
								}
							/>
							<CheckboxText>
								{t(
									"orderPageWidgets.parking.filter.popover.content.str103",
								) ?? ""}
							</CheckboxText>
						</StyledRow>
						<StyledRow gap="10px">
							<CheckBox
								onChange={onChangeDinner}
								value={isDinner}
								disabled={
									!checkFlagAccessControl[TabKeys.STATUS]
								}
							/>
							<CheckboxText>
								{t(
									"orderPageWidgets.parking.filter.popover.content.str104",
								) ?? ""}
							</CheckboxText>
						</StyledRow>
						<StyledRow gap="10px">
							<CheckBox
								onChange={onChangeHome}
								value={isHome}
								disabled={
									!checkFlagAccessControl[TabKeys.STATUS]
								}
							/>
							<CheckboxText>
								{t(
									"orderPageWidgets.parking.filter.popover.content.str105",
								) ?? ""}
							</CheckboxText>
						</StyledRow>
						<StyledRow gap="10px">
							<CheckBox
								onChange={onChangeOnOrder}
								value={isOnOrder}
								disabled={
									!checkFlagAccessControl[TabKeys.STATUS]
								}
							/>
							<CheckboxText>
								{t(
									"orderPageWidgets.parking.filter.popover.content.str106",
								) ?? ""}
							</CheckboxText>
						</StyledRow>
						<StyledRow gap="10px">
							<CheckBox
								onChange={onChangeClosed}
								value={isClosed}
								disabled={
									!checkFlagAccessControl[TabKeys.STATUS]
								}
							/>
							<CheckboxText>
								{t(
									"orderPageWidgets.parking.filter.popover.content.str107",
								) ?? ""}
							</CheckboxText>
						</StyledRow>
						<StyledRow gap="10px">
							<CheckBox
								onChange={onChangeIsYourOrderStatus}
								value={isYourOrderStatus}
								disabled={
									!checkFlagAccessControl[TabKeys.STATUS]
								}
							/>
							<CheckboxText>
								{t(
									"orderPageWidgets.parking.filter.popover.content.str108",
								) ?? ""}
							</CheckboxText>
						</StyledRow>
					</StyledColumn>
				</FieldsContainer>
			</StyledColumn>
			<StyledColumn onClick={closeCompanyAndTaxiService} w="100%">
				<FieldsContainer
					label={
						t(
							"orderPageWidgets.parking.filter.popover.content.str6",
						) ?? ""
					}
					labelColor={theme.colors.text_placeholder}
					maxedHeight
				>
					<StyledColumn gap="12px">
						<StyledRow gap="10px">
							<CheckBox
								onChange={onChangeWorking}
								value={isWorkingStatus}
								disabled={
									!checkFlagAccessControl[TabKeys.IS_WORKING]
								}
							/>
							<CheckboxText>
								{t(
									"orderPageWidgets.parking.filter.popover.content.str109",
								) ?? ""}
							</CheckboxText>
						</StyledRow>
						<StyledRow gap="10px">
							<CheckBox
								onChange={onChangeNotWorking}
								value={isNotWorkingStatus}
								disabled={
									!checkFlagAccessControl[TabKeys.IS_WORKING]
								}
							/>
							<CheckboxText>
								{t(
									"orderPageWidgets.parking.filter.popover.content.str110",
								) ?? ""}
							</CheckboxText>
						</StyledRow>
					</StyledColumn>
				</FieldsContainer>
			</StyledColumn>
		</StyledGrid>
	);
};

declare namespace Content {
	interface Value extends Pick<useCarBrandSubscription.Filter, "activity"> {}

	export interface PropsBase {}

	interface Props {
		taxiServiceData: TaxiService.Model[] | undefined;
		taxiServiceIdsPosition: number[];
		language: Language;
		onChangeRequestData: React.Dispatch<
			React.SetStateAction<ParkingFilter>
		>;
		possibleCompanies: Company.Model[];
		possibleTaxiServices: TaxiService.Model[];
		companyIds: number[];
		taxiServiceIds: number[];
		setCompanyIds: (newCompanyIds: number[]) => void;
		setTaxiServiceIds: (newTaxiServiceIds: number[]) => void;
		taxiServiceIdsTablePosition: number[];
		setTaxiServiceIdsTablePosition: React.Dispatch<
			React.SetStateAction<number[]>
		>;
		executorsStatuses: Content.ExecutorStatus[];
		setExecutorsStatuses: React.Dispatch<
			React.SetStateAction<Content.ExecutorStatus[]>
		>;
		error: {
			taxiService: boolean;
			company: boolean;
		};
		onChangeError: React.Dispatch<
			React.SetStateAction<{
				taxiService: boolean;
				company: boolean;
			}>
		>;
		onlineStatus: ("online" | "offline")[];
		setOnlineStatus: React.Dispatch<
			React.SetStateAction<("online" | "offline")[]>
		>;
		workingStatus: ("working" | "notWorking")[];
		setWorkingStatus: React.Dispatch<
			React.SetStateAction<("working" | "notWorking")[]>
		>;
		isYourOrderStatus: boolean;
		setIsYourOrderStatus: React.Dispatch<React.SetStateAction<boolean>>;

		executorGroupIds: number[];
		setExecutorGroupIds: Dispatch<number[]>;

		setShowModalPropsForExecutorGroup: (value: boolean) => void;
	}

	type ExecutorStatus =
		| "available"
		| "busy"
		| "dinner"
		| "home"
		| "on_order"
		| "closed";

	type OnlineStatus = "online" | "offline";
	type WorkingStatus = "working" | "notWorking";
}

export default Content;
